import React from "react";
import Layout from "../../components/shared/layout/layout";
const TermsOfService = () => {
  const lastUpdated = "14 October 2025";

  const Section = ({ number, title, children }) => (
    <section className="mb-10">
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-semibold">
          {number}
        </div>

        <h2 className="text-xl md:text-2xl font-semibold text-slate-900 pt-1">
          {title}
        </h2>
      </div>

      <div className="md:pl-13 text-slate-600 leading-7 space-y-4">
        {children}
      </div>
    </section>
  );

  return (
     <Layout>
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className="bg-slate-950 text-white">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="max-w-3xl">
            <p className="text-sm font-medium tracking-widest uppercase text-slate-400 mb-4">
              Zulfah Academy
            </p>

            <h1 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              Enrollment Terms & Conditions
            </h1>

            <p className="mt-5 text-slate-300 text-base md:text-lg leading-8">
              Please read these terms carefully before enrolling in any
              programme, course, bootcamp, workshop, mentorship, internship,
              corporate training, or other learning programme provided by
              Zulfah Academy.
            </p>

            <p className="mt-6 text-sm text-slate-400">
              Last updated: {lastUpdated}
            </p>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-5 md:px-8 py-12">
        {/* Important notice */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 mb-12 shadow-sm">
          <p className="text-xs font-bold tracking-widest uppercase text-slate-500 mb-3">
            Important Notice
          </p>

          <p className="text-slate-700 leading-7">
            By submitting an enrollment application, making any payment,
            commencing a programme, accessing our learning platforms, or
            otherwise participating in a Zulfah Academy programme, you
            acknowledge that you have read, understood and agreed to be bound
            by these Enrollment Terms & Conditions.
          </p>

          <p className="text-slate-700 leading-7 mt-4">
            If a student is under the age of 18, the student's parent or legal
            guardian must review and accept these Terms on the student's
            behalf and shall be responsible for the financial and other
            obligations applicable to the enrollment.
          </p>
        </div>

        <Section number="1" title="About These Terms">
          <p>
            These Enrollment Terms & Conditions ("Terms") constitute an
            agreement between the person enrolled in a programme ("Student",
            "you", or "your") and Zulfah Academy ("Zulfah", "Academy", "we",
            "us", or "our").
          </p>

          <p>
            These Terms apply to all training and educational services offered
            by Zulfah Academy, whether delivered physically, online, remotely,
            through a hybrid arrangement, or through any other approved
            learning format.
          </p>

          <p>
            Where a separate programme offer, invoice, scholarship letter,
            payment plan, corporate training agreement, or written special
            arrangement has been issued to you, that document shall be read
            together with these Terms.
          </p>
        </Section>

        <Section number="2" title="Enrollment and Admission">
          <p>
            Enrollment is subject to the Academy's admission requirements,
            programme capacity, payment requirements, and any prerequisites
            communicated for the relevant programme.
          </p>

          <p>
            Submission of an application does not by itself guarantee
            admission. Zulfah Academy reserves the right to accept or decline
            an application where reasonably necessary, including where
            programme requirements have not been satisfied or capacity has
            been reached.
          </p>

          <p>
            Students are responsible for providing accurate, complete and
            truthful information during enrollment. Providing false,
            misleading, fraudulent, or materially incomplete information may
            result in suspension or cancellation of enrollment.
          </p>

          <p>
            Enrollment is personal to the Student and may not be transferred
            to another person without prior written approval from Zulfah
            Academy.
          </p>
        </Section>

        <Section number="3" title="Programme Fees and Payment">
          <p>
            The Student agrees to pay all tuition, registration, examination,
            certification, material, accommodation, equipment, or other fees
            specifically communicated as applicable to the selected programme.
          </p>

          <p>
            A Student's place may not be regarded as confirmed until the
            required initial payment, deposit, or full programme fee has been
            received and confirmed by Zulfah Academy.
          </p>

          <p>
            Where an installment plan is approved, each installment must be
            paid on or before its agreed due date. An installment arrangement
            does not reduce the total programme fee unless Zulfah Academy
            expressly agrees otherwise in writing.
          </p>

          <p>
            Failure to make an installment when due may result in temporary
            restriction of access to classes, learning platforms, mentorship,
            assessments, examinations, projects, certificates, or other
            programme services until the outstanding amount is resolved.
          </p>

          <p>
            The Student remains responsible for approved outstanding fees
            notwithstanding temporary suspension, absence from classes, or
            failure to complete the programme, except where otherwise required
            by applicable law or expressly agreed by Zulfah Academy in writing.
          </p>

          <p>
            Bank charges, payment gateway charges, foreign exchange
            differences, or other transaction charges imposed by third-party
            financial institutions may be borne by the payer where applicable.
          </p>
        </Section>

        <Section number="4" title="Refund and Cancellation Policy">
          <p>
            Programme fees are used to reserve training capacity, instructors,
            facilities, technology, administration, learning resources and
            other programme commitments. Refund requests are therefore subject
            to the applicable programme refund policy and these Terms.
          </p>

          <p>
            Except where otherwise stated in writing or required by applicable
            law, registration fees, application fees, administrative charges,
            and fees expressly described as non-refundable are not refundable.
          </p>

          <p>
            Where a Student voluntarily withdraws before a programme begins,
            any refund that may be available will be determined in accordance
            with the refund terms communicated for that programme and may be
            subject to reasonable administrative deductions.
          </p>

          <p>
            Once a Student has commenced or materially accessed the programme,
            including attending classes or accessing substantial course
            materials, recordings, mentorship, projects, or learning
            platforms, amounts already paid may become non-refundable to the
            extent permitted by applicable law and the applicable programme
            refund policy.
          </p>

          <p>
            Failure to attend classes, change of personal schedule, relocation,
            employment commitments, loss of interest, failure to obtain
            personal equipment, or other circumstances outside the Academy's
            control do not automatically create an entitlement to a refund.
          </p>

          <p>
            Where Zulfah Academy cancels an entire programme before
            commencement and does not provide a reasonable alternative,
            affected Students will be offered an appropriate remedy, which may
            include transfer, credit, rescheduling, or refund of the affected
            programme fee as appropriate and subject to applicable law.
          </p>
        </Section>

        <Section number="5" title="Deferral and Programme Transfer">
          <p>
            A Student who cannot continue a programme may submit a written
            request for deferral. Deferral is not automatic and is subject to
            approval, programme availability, academic progress, payment
            status, and any applicable administrative requirements.
          </p>

          <p>
            Where approved, a deferral may permit the Student to resume with a
            later cohort within the period specified by Zulfah Academy.
          </p>

          <p>
            Any difference in tuition or programme fees arising from a later
            cohort, revised curriculum, additional training, or change of
            programme may be payable by the Student.
          </p>

          <p>
            Students may not repeatedly defer enrollment or indefinitely
            reserve a place in future cohorts.
          </p>
        </Section>

        <Section number="6" title="Attendance and Participation">
          <p>
            Students are expected to attend scheduled classes, practical
            sessions, assessments, mentorship sessions and other required
            programme activities.
          </p>

          <p>
            Students are responsible for catching up on missed work and
            notifying the Academy where prolonged absence is unavoidable.
          </p>

          <p>
            Zulfah Academy may prescribe a minimum attendance requirement for
            certification, graduation, internship placement, recommendation,
            or progression to another stage of a programme.
          </p>

          <p>
            Repeated lateness, unexplained absence, non-participation, or
            failure to complete required assessments may affect the Student's
            eligibility to complete or graduate from the programme.
          </p>
        </Section>

        <Section number="7" title="Academic Requirements and Assessment">
          <p>
            Students must complete assignments, projects, examinations,
            practical exercises, presentations, assessments and other
            requirements prescribed for their programme.
          </p>

          <p>
            Zulfah Academy may determine reasonable assessment methods,
            grading criteria, project requirements and completion standards
            appropriate to each programme.
          </p>

          <p>
            Submission of plagiarized work, impersonation, unauthorized
            collaboration, examination misconduct, falsification of project
            work, use of another person's work as one's own, or other forms of
            academic dishonesty may result in disciplinary action.
          </p>

          <p>
            Depending on the seriousness of the misconduct, disciplinary
            action may include reassessment, failure of an assessment,
            suspension, withholding of certification, or termination of
            enrollment.
          </p>
        </Section>

        <Section number="8" title="Certificates and Completion">
          <p>
            Enrollment or payment alone does not guarantee the award of a
            certificate.
          </p>

          <p>
            Certificates may be issued only to Students who satisfy the
            applicable programme requirements, which may include attendance,
            assessments, projects, conduct, completion requirements and
            settlement of outstanding programme fees.
          </p>

          <p>
            Zulfah Academy may verify the authenticity of certificates it has
            issued and may revoke a certificate obtained through fraud,
            impersonation, falsified records, or serious academic misconduct,
            subject to applicable law and appropriate review.
          </p>
        </Section>

        <Section number="9" title="Student Conduct">
          <p>
            Students must treat instructors, staff, fellow Students, visitors
            and partners with professionalism and respect.
          </p>

          <p>
            Harassment, bullying, threats, discrimination, violence, theft,
            deliberate damage, fraud, sexual misconduct, disruptive behaviour,
            unlawful conduct, or conduct that creates a material safety risk
            will not be tolerated.
          </p>

          <p>
            Students must comply with reasonable Academy rules relating to
            safety, security, equipment, facilities, online communities,
            communication channels and classroom conduct.
          </p>

          <p>
            Zulfah Academy may investigate reported misconduct and may impose
            proportionate disciplinary measures, including warnings,
            temporary suspension or termination of enrollment.
          </p>
        </Section>

        <Section number="10" title="Technology, Equipment and Internet Access">
          <p>
            Unless expressly stated otherwise, Students are responsible for
            obtaining and maintaining the computer, software, internet
            connection, electricity, accessories and other equipment necessary
            to participate effectively in the programme.
          </p>

          <p>
            Students are responsible for protecting their devices, accounts,
            passwords, files and personal data.
          </p>

          <p>
            The Academy is not responsible for loss caused solely by a
            Student's device failure, internet outage, loss of credentials,
            incompatible personal equipment, or third-party service failure
            outside the Academy's reasonable control.
          </p>
        </Section>

        <Section number="11" title="Learning Accounts and Access">
          <p>
            Where the Academy provides access to a learning management system,
            repository, communication platform, cloud environment, licensed
            software, or other digital resource, such access is provided for
            the Student's authorized educational use.
          </p>

          <p>
            Login credentials must not be sold, transferred, published or
            shared with unauthorized persons.
          </p>

          <p>
            Zulfah Academy may suspend or revoke access where there is
            reasonable evidence of unauthorized sharing, misuse, security
            abuse, non-payment, or termination of enrollment.
          </p>
        </Section>

        <Section number="12" title="Intellectual Property and Course Materials">
          <p>
            Unless otherwise expressly stated, Zulfah Academy and/or its
            licensors retain all rights in Academy-created course materials,
            curriculum, videos, recordings, slides, templates, exercises,
            assessments, documentation, software examples, branding and other
            educational content provided to Students.
          </p>

          <p>
            Enrollment grants the Student a limited, personal,
            non-exclusive and non-transferable right to use those materials
            for the Student's own learning.
          </p>

          <p>
            Students may not reproduce, resell, commercially distribute,
            publish, upload, sublicense, substantially copy, or operate a
            competing training programme using protected Zulfah Academy
            materials without prior written authorization.
          </p>

          <p>
            Unless otherwise agreed in writing, a Student retains ownership of
            original projects and work independently created by that Student.
            This does not transfer ownership of pre-existing Academy
            materials, frameworks, templates, code, datasets, or intellectual
            property incorporated into or supplied for such projects.
          </p>
        </Section>

        <Section number="13" title="Recording of Classes">
          <p>
            Certain online or physical classes may be recorded for educational,
            quality assurance, security, documentation, or internal training
            purposes where appropriate.
          </p>

          <p>
            Where Students may be identifiable in recordings intended for
            promotional or public marketing use, Zulfah Academy will obtain
            any consent required by applicable law.
          </p>

          <p>
            Students may not secretly record, redistribute, sell, publish or
            commercially exploit Academy classes or private conversations
            without authorization.
          </p>
        </Section>

        <Section number="14" title="Photos, Testimonials and Publicity">
          <p>
            Zulfah Academy may invite Students or alumni to participate in
            photographs, videos, testimonials, interviews, showcases,
            graduation materials and success stories.
          </p>

          <p>
            Where consent is required for promotional use of identifiable
            images, recordings or testimonials, the Academy will seek the
            appropriate permission. A Student may decline optional promotional
            participation without affecting ordinary academic treatment.
          </p>
        </Section>

        <Section number="15" title="Internships, Employment and Career Outcomes">
          <p>
            Zulfah Academy may provide career guidance, portfolio support,
            mentorship, interview preparation, referrals, internship
            opportunities, employer introductions, job information or other
            career-support services.
          </p>

          <p>
            Unless Zulfah Academy expressly provides a separate written
            guarantee, enrollment does not constitute a guarantee of
            employment, internship placement, promotion, salary level,
            immigration outcome, freelance income, business success or any
            other specific career result.
          </p>

          <p>
            Employment and career outcomes depend on multiple factors outside
            the Academy's control, including Student performance, market
            conditions, employer requirements, interview performance and
            individual circumstances.
          </p>
        </Section>

        <Section number="16" title="Programme Changes">
          <p>
            Technology and professional practice evolve rapidly. Zulfah
            Academy may reasonably update programme content, curriculum,
            software tools, instructors, schedules, delivery methods,
            assessment methods or learning resources to maintain programme
            relevance and quality.
          </p>

          <p>
            Where a material change significantly affects a Student's
            programme, the Academy will use reasonable efforts to communicate
            the change and provide an appropriate alternative where necessary.
          </p>
        </Section>

        <Section number="17" title="Suspension and Termination">
          <p>
            Zulfah Academy may suspend or terminate a Student's enrollment for
            material breach of these Terms, serious misconduct, fraud,
            academic dishonesty, persistent non-payment, threats to safety,
            unauthorized commercial use of Academy materials, or other serious
            violations.
          </p>

          <p>
            Except where immediate action is reasonably necessary for safety,
            security, fraud prevention or serious misconduct, the Academy may
            provide the Student an opportunity to respond to the relevant
            concern before final termination.
          </p>

          <p>
            Termination for Student misconduct does not automatically create a
            right to refund amounts properly earned or already applied to
            services delivered, subject always to applicable law.
          </p>
        </Section>

        <Section number="18" title="Health, Safety and Property">
          <p>
            Students attending physical locations must follow reasonable
            health, safety, access-control and emergency procedures.
          </p>

          <p>
            Students remain responsible for their personal belongings.
            Zulfah Academy will not be responsible for loss, theft or damage
            to personal property except to the extent liability cannot
            lawfully be excluded.
          </p>
        </Section>

        <Section number="19" title="Privacy and Personal Information">
          <p>
            Zulfah Academy may collect and process information reasonably
            necessary for enrollment, payment administration, programme
            delivery, communication, assessment, certification, student
            support, security, regulatory compliance and legitimate Academy
            operations.
          </p>

          <p>
            Personal information will be handled in accordance with applicable
            data protection requirements and the Academy's Privacy Policy,
            where applicable.
          </p>

          <p>
            Students are responsible for keeping their contact information
            current so that important programme communications can reach them.
          </p>
        </Section>

        <Section number="20" title="Third-Party Platforms and Services">
          <p>
            Some programmes may use third-party services such as video
            conferencing tools, cloud services, code repositories, payment
            processors, productivity software, certification platforms or
            communication tools.
          </p>

          <p>
            Those third-party services may be governed by their own terms and
            privacy policies. Zulfah Academy is not responsible for outages,
            policy changes or failures caused solely by independent
            third-party providers outside its reasonable control.
          </p>
        </Section>

        <Section number="21" title="Limitation of Liability">
          <p>
            To the maximum extent permitted by applicable law, Zulfah
            Academy's liability arising directly from a programme shall be
            limited to losses that are reasonably foreseeable and directly
            attributable to the Academy's breach of its obligations.
          </p>

          <p>
            Zulfah Academy will not be liable for indirect or consequential
            losses, lost profits, lost business opportunities, loss arising
            from a Student's independent career or investment decisions, or
            failures caused by circumstances outside the Academy's reasonable
            control, except where such liability cannot legally be excluded or
            limited.
          </p>

          <p>
            Nothing in these Terms excludes or limits liability that cannot be
            excluded or limited under applicable law.
          </p>
        </Section>

        <Section number="22" title="Student Responsibility and Indemnity">
          <p>
            Students are responsible for their own unlawful acts, misuse of
            Academy systems, unauthorized infringement of third-party rights,
            and material violations of these Terms.
          </p>

          <p>
            To the extent permitted by law, a Student may be responsible for
            reasonable losses or claims suffered by Zulfah Academy that arise
            directly from the Student's fraud, deliberate misconduct,
            unauthorized commercial exploitation of Academy intellectual
            property, or unlawful use of Academy systems.
          </p>
        </Section>

        <Section number="23" title="Force Majeure">
          <p>
            Zulfah Academy shall not be treated as being in breach solely
            because performance is delayed or prevented by circumstances
            reasonably beyond its control, including serious infrastructure
            failures, government restrictions, public emergencies, natural
            disasters, widespread internet or power disruption, industrial
            action, civil disturbance or similar events.
          </p>

          <p>
            Where such circumstances materially affect programme delivery, the
            Academy may reasonably reschedule classes, move delivery online,
            provide replacement sessions, extend programme dates, or adopt
            another reasonable method of completing the affected training.
          </p>
        </Section>

        <Section number="24" title="Communications and Notices">
          <p>
            Official programme communications may be sent through email,
            telephone, SMS, WhatsApp, the Academy's learning platform, student
            portal, or other contact channels supplied by the Student.
          </p>

          <p>
            Students are responsible for monitoring the contact channels they
            provide and notifying the Academy when their details change.
          </p>
        </Section>

        <Section number="25" title="Complaints and Dispute Resolution">
          <p>
            Students are encouraged to raise concerns promptly with Zulfah
            Academy so that they can be reviewed and resolved fairly.
          </p>

          <p>
            Where a dispute arises, both parties should first attempt in good
            faith to resolve the matter through direct discussion or the
            Academy's internal complaint process.
          </p>

          <p>
            Where the dispute cannot be resolved internally, the parties may
            pursue mediation, arbitration, or another legally available
            dispute-resolution mechanism where appropriate and agreed or
            permitted by applicable law.
          </p>
        </Section>

        <Section number="26" title="Governing Law">
          <p>
            These Terms shall be governed by and interpreted in accordance
            with the laws of the Federal Republic of Nigeria, subject to any
            mandatory rights or protections applicable to the Student.
          </p>
        </Section>

        <Section number="27" title="Changes to These Terms">
          <p>
            Zulfah Academy may update these Terms from time to time to reflect
            changes in its programmes, operations, technology, regulatory
            obligations or policies.
          </p>

          <p>
            Material changes affecting an existing Student's rights or
            obligations will not be applied unfairly or retrospectively where
            doing so would be inconsistent with applicable law.
          </p>
        </Section>

        <Section number="28" title="Severability and Waiver">
          <p>
            If any provision of these Terms is found to be invalid,
            unenforceable or unlawful, the remaining provisions shall continue
            to apply to the extent legally permitted.
          </p>

          <p>
            A delay or failure by Zulfah Academy to enforce a provision on one
            occasion does not automatically constitute a permanent waiver of
            that provision.
          </p>
        </Section>

        <Section number="29" title="Entire Enrollment Agreement">
          <p>
            These Terms, together with the Student's enrollment information,
            applicable programme details, invoice, payment plan, refund policy,
            privacy policy and any written special terms accepted by the
            parties, constitute the applicable enrollment arrangement between
            the Student and Zulfah Academy.
          </p>
        </Section>

        <Section number="30" title="Electronic Acceptance">
          <p>
            By selecting an "I Agree", "Accept Terms", "Enroll", "Proceed to
            Payment", or similar confirmation option, the Student confirms that
            they have had the opportunity to read these Terms and agrees to be
            bound by them.
          </p>

          <p>
            Electronic acceptance may be recorded together with information
            such as the Student's account, date and time of acceptance, version
            of the Terms accepted, and other reasonable audit information.
          </p>
        </Section>

        {/* Acceptance Box */}
        <div className="mt-14 bg-slate-950 text-white rounded-3xl p-7 md:p-10">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-400">
            Enrollment Declaration
          </p>

          <h2 className="text-2xl md:text-3xl font-bold mt-3">
            By enrolling, you agree to these terms.
          </h2>

          <p className="mt-5 text-slate-300 leading-7 max-w-3xl">
            I confirm that I have read and understood the Zulfah Academy
            Enrollment Terms & Conditions. I understand my financial,
            academic and conduct obligations and agree to comply with the
            policies applicable to my programme.
          </p>
        </div>

        {/* Contact */}
        <div className="mt-10 border-t border-slate-200 pt-8 text-sm text-slate-500">
          <p className="font-semibold text-slate-800 mb-2">
            Questions about these Terms?
          </p>

          <p>
            Please contact Zulfah Academy through the official contact details
            provided on our website or student enrollment portal.
          </p>
        </div>
      </main>
    </div>
    </Layout>
  );
};

export default TermsOfService;